/**
 * Pure extraction → finalized-record diff for the auto-fill correction-tracking table.
 *
 * No React / Supabase imports — fully unit-testable AND importable by a service-role backfill script. The
 * service flattens the FINAL record (line items + recon scalars) into `FinalFeedbackItem[]`, this module
 * flattens the cached extraction and diffs the two deterministically (no fuzzy scorer).
 *
 * Scope (v2): the three money categories where extraction misses actually happen — warehouse_cost,
 * investor_cost, wire. Dates/price and Section-A are intentionally out of scope. `missed` is attributed to a
 * single owning doc (the warehouse/combined doc owns all three; the PA owns none), so a final row is never
 * double-counted across the two documents.
 */

import { INVESTOR_FEE_OPTIONS, type ExtractedFields, type FieldConfidence } from './types';
import {
  canonicalInvestorCostBaseType,
  normalizeExtractedFieldsForBuydown,
  normalizeExtractedInvestorCostType
} from '@/utils/banker-recon/buydownPassThrough';

export type FeedbackStatus = 'accepted' | 'corrected' | 'dropped' | 'missed';
export type FeedbackCategory = 'warehouse_cost' | 'investor_cost' | 'wire';

export interface ExtractionFeedbackItem {
  /** extraction_item_key — stable: warehouse line_type, 'wireFromInvestor', or 'investorCosts[<i>]'. */
  key: string;
  category: FeedbackCategory;
  baseType: string;
  value: number;
  label?: string;
  confidence?: number;
}

export interface FinalFeedbackItem {
  category: FeedbackCategory;
  baseType: string;
  value: number;
}

/** The diff-specific columns of a feedback row (the service adds per-doc context before insert). */
export interface FeedbackRow {
  extraction_item_key: string | null;
  category: string;
  base_type: string;
  source_label: string | null;
  extracted_value: number | null;
  extracted_confidence: number | null;
  applied_value: number | null;
  applied_category: string | null;
  status: FeedbackStatus;
}

// ExtractedFields key -> warehouse line_type. Mirrors the mapper's WAREHOUSE_FEE_MAP.
const WAREHOUSE_FEE_KEYS: Array<{ field: keyof ExtractedFields; lineType: string; label: string }> = [
  { field: 'warehouseInterest', lineType: 'warehouse_interest', label: 'Warehouse Interest' },
  { field: 'fundingFee', lineType: 'warehouse_funding_fee', label: 'Warehouse Funding Fee' },
  { field: 'reconciliationFee', lineType: 'warehouse_recon_fee', label: 'Reconciliation Report Fee' },
  { field: 'delegatedUWFee', lineType: 'warehouse_du_fee', label: 'Delegated Underwriter Fee' },
  { field: 'collateralDwellFee', lineType: 'collateral_dwell_fee', label: 'Collateral Dwell Fee' },
  { field: 'shortageFunded', lineType: 'shortage_funded', label: 'Shortage Funded at Closing' }
];

const INVESTOR_FEE_IDS = new Set<string>(INVESTOR_FEE_OPTIONS.map((o) => o.id));
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const AMOUNT_TOLERANCE = 0.01;

/** The categories a document type owns for `missed` detection (final-present-but-never-extracted). */
export function ownedMissedCategories(isPurchaseAdvice: boolean): Set<FeedbackCategory> {
  // The warehouse/combined doc is the comprehensive owner; the PA owns none (so investor-cost/wire that live
  // on the warehouse recon are never flagged missed against the PA).
  return isPurchaseAdvice ? new Set() : new Set(['warehouse_cost', 'investor_cost', 'wire']);
}

/** Flatten a cached extraction's money fields into comparable items (omits zero — matches the mapper). */
export function flattenExtractionForFeedback(
  fields: ExtractedFields,
  fieldConfidence: FieldConfidence = {}
): ExtractionFeedbackItem[] {
  const normalizedFields = normalizeExtractedFieldsForBuydown(fields);
  const items: ExtractionFeedbackItem[] = [];
  const conf = (k: string): number | undefined => (isNum(fieldConfidence[k]) ? fieldConfidence[k] : undefined);

  for (const fee of WAREHOUSE_FEE_KEYS) {
    const v = normalizedFields[fee.field];
    if (isNum(v) && v !== 0) {
      items.push({
        key: fee.lineType,
        category: 'warehouse_cost',
        baseType: fee.lineType,
        value: v,
        label: fee.label,
        confidence: conf(fee.field as string)
      });
    }
  }

  if (isNum(normalizedFields.wireFromInvestor) && normalizedFields.wireFromInvestor !== 0) {
    items.push({ key: 'wireFromInvestor', category: 'wire', baseType: 'wireFromInvestor', value: normalizedFields.wireFromInvestor, confidence: conf('wireFromInvestor') });
  }
  if (isNum(normalizedFields.wireFromWarehouse) && normalizedFields.wireFromWarehouse !== 0) {
    items.push({ key: 'wireFromWarehouse', category: 'wire', baseType: 'wireFromWarehouse', value: normalizedFields.wireFromWarehouse, confidence: conf('wireFromWarehouse') });
  }

  const investorCosts = normalizedFields.investorCosts ?? [];
  for (let i = 0; i < investorCosts.length; i++) {
    const ic = investorCosts[i];
    if (!ic || !isNum(ic.amount) || ic.amount === 0) {
      continue;
    }
    const normalizedType = normalizeExtractedInvestorCostType(ic.type, ic.label);
    const type = INVESTOR_FEE_IDS.has(normalizedType) ? normalizedType : 'purchase_fee';
    items.push({
      key: `investorCosts[${i}]`,
      category: 'investor_cost',
      baseType: type,
      value: ic.amount,
      label: typeof ic.label === 'string' ? ic.label : undefined,
      confidence: conf(`investorCosts[${i}]`)
    });
  }

  return items;
}

function mkRow(e: ExtractionFeedbackItem, f: FinalFeedbackItem | null, status: FeedbackStatus): FeedbackRow {
  return {
    extraction_item_key: e.key,
    category: e.category,
    base_type: e.baseType,
    source_label: e.label ?? null,
    extracted_value: e.value,
    extracted_confidence: e.confidence ?? null,
    applied_value: f ? f.value : null,
    applied_category: f ? f.category : null,
    status
  };
}

/**
 * Deterministic per-(category, baseType) comparison. equal→accepted, both-present-different→corrected,
 * extracted-not-final→dropped, final-not-extracted-in-owned-category→missed. No fuzzy/N:M scoring.
 */
/**
 * Build the FINAL money items (warehouse_cost + investor_cost line items + wires) from the finalized record.
 * Pure — shared by the live capture and the backfill so neither reimplements the flatten.
 */
export function buildFinalFeedbackItems(
  lineItems: Array<{ category: string; line_type: string; amount: number | string; description?: string }>,
  wireFromInvestor: number | string | null,
  wireFromWarehouse: number | string | null
): FinalFeedbackItem[] {
  const items: FinalFeedbackItem[] = [];
  for (const li of lineItems) {
    const base = li.category === 'investor_cost'
      ? canonicalInvestorCostBaseType(li.line_type, li.description)
      : li.line_type.replace(/_\d+$/, '');
    const amount = Number(li.amount);
    if (!Number.isFinite(amount)) {
      continue;
    }
    if (li.category === 'warehouse_cost') {
      items.push({ category: 'warehouse_cost', baseType: base, value: amount });
    } else if (li.category === 'investor_cost') {
      items.push({ category: 'investor_cost', baseType: base, value: amount });
    }
  }
  const wInv = Number(wireFromInvestor);
  const wWh = Number(wireFromWarehouse);
  if (Number.isFinite(wInv) && wInv !== 0) {
    items.push({ category: 'wire', baseType: 'wireFromInvestor', value: wInv });
  }
  if (Number.isFinite(wWh) && wWh !== 0) {
    items.push({ category: 'wire', baseType: 'wireFromWarehouse', value: wWh });
  }
  return items;
}

export function diffExtractionVsFinal(
  extraction: ExtractionFeedbackItem[],
  final: FinalFeedbackItem[],
  owned: Set<FeedbackCategory>
): FeedbackRow[] {
  const rows: FeedbackRow[] = [];
  const finalByKey = new Map<string, FinalFeedbackItem>();
  for (const f of final) {
    finalByKey.set(`${f.category}:${f.baseType}`, f);
  }
  const matched = new Set<string>();

  for (const e of extraction) {
    const k = `${e.category}:${e.baseType}`;
    const f = finalByKey.get(k);
    if (f) {
      matched.add(k);
      rows.push(mkRow(e, f, Math.abs(e.value - f.value) <= AMOUNT_TOLERANCE ? 'accepted' : 'corrected'));
    } else {
      rows.push(mkRow(e, null, 'dropped'));
    }
  }

  for (const f of final) {
    const k = `${f.category}:${f.baseType}`;
    if (matched.has(k) || !owned.has(f.category)) {
      continue;
    }
    rows.push({
      extraction_item_key: null,
      category: f.category,
      base_type: f.baseType,
      source_label: null,
      extracted_value: null,
      extracted_confidence: null,
      applied_value: f.value,
      applied_category: f.category,
      status: 'missed'
    });
  }

  return rows;
}
